// The admin's view of who reads what: the site's own count, kept without a
// cookie or a third party (server/src/stats/routes.ts). To anyone else there
// is no such page.
//
// One series — views by day — so one hue, the accent, as every chart on the
// site has it; the numbers wear the ink tokens. The tiles lead, the range sits
// in one row above what it scopes, and every figure in the chart is also in a
// table beneath it.
import {
  parseRef,
  statsPageLabel as pageLabel,
  statsRanges,
  toDisplay,
  type EventType,
  type StatsRange,
  type StatsSource,
  type StatsTotals,
  type StatsView,
  type ToolUsageView,
} from "@bibleunleashed/shared";
import { useEffect, useState } from "react";
import { niceTicks } from "@/charts/scale";
import { Header } from "@/components/Header";
import { useUser } from "@/lib/auth";
import { useSiteStats, useToolUsage } from "@/lib/stats";
import { Placeholder } from "./Placeholder";

const format = new Intl.NumberFormat("en-GB").format;
const dayFormat = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", day: "numeric", month: "short" });
const fullDayFormat = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short", year: "numeric" });
const dayLabel = (day: string, full = false) => (full ? fullDayFormat : dayFormat).format(new Date(`${day}T00:00:00Z`));

const RANGE_LABEL: Record<StatsRange, string> = { 7: "7 days", 30: "30 days", 90: "90 days", 365: "12 months" };

const SOURCE_LABEL: Record<StatsSource, string> = { direct: "Direct, or nowhere named", facebook: "Facebook", search: "Search engines", other: "Other sites" };

const TOOL_LABEL: Record<EventType, string> = {
  search: "Search",
  study_crossrefs: "Cross-references opened",
  study_translations: "Other translations",
  study_word: "Word study",
  audio_play: "Audio played",
  highlight: "Highlights",
  note: "Notes",
  bookmark: "Bookmarks",
  sign_in: "Sign-ins",
  glossary_term: "Glossary words opened",
};

/** "Nehemiah 1:3" for a verse reference; anything that will not parse as it is. */
const verseLabel = (osis: string): string => {
  try {
    return toDisplay(parseRef(osis));
  } catch {
    return osis;
  }
};

export function Stats() {
  const { data: user, isLoading: userLoading } = useUser();
  const [days, setDays] = useState<StatsRange>(30);
  const isAdmin = user?.role === "admin";
  const { data, isLoading, error } = useSiteStats(days);

  useEffect(() => {
    document.title = "Site statistics · The Bible Unleashed";
  }, []);

  if (!isAdmin) return userLoading ? <Header /> : <Placeholder title="Page not found" />;

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-[960px] px-6 py-14 font-sans lg:px-10">
        <p className="text-xs tracking-[0.12em] text-ink-3 uppercase">Admin</p>
        <h1 className="mt-1.5 font-serif text-[40px] font-semibold tracking-[-0.015em]">Site statistics</h1>
        <p className="mt-2 max-w-[620px] text-[13.5px] leading-relaxed text-ink-2">
          Counted by the site itself, with no cookie and no third party. A visitor is a hash of address and browser
          under a key that changes every day and is then thrown away, so a reader is counted afresh each day and can
          never be followed between days. Your own reading while signed in is left out, as are crawlers, link previewers and visits from datacenters. Days are UTC.
        </p>

        <div role="group" aria-label="Range" className="mt-8 flex flex-wrap gap-1.5">
          {statsRanges.map((n) => (
            <button
              key={n}
              onClick={() => setDays(n)}
              aria-pressed={n === days}
              className={`rounded-md border px-3 py-1.5 text-[13px] ${n === days ? "border-ink bg-ink text-paper" : "border-rule text-ink-2 hover:border-ink-3 hover:text-ink"}`}
            >
              {RANGE_LABEL[n]}
            </button>
          ))}
        </div>

        {isLoading && <p className="mt-8 text-[13px] text-ink-3">Counting…</p>}
        {error && <p className="mt-8 text-[13px] text-ink-2">Couldn’t load the statistics: {error.message}</p>}
        {data && <Report stats={data} />}
        <ToolUsage days={days} />
      </main>
    </div>
  );
}

function Report({ stats }: { stats: StatsView }) {
  const span = `${dayLabel(stats.from)} – ${dayLabel(stats.to)}`;
  return (
    <>
      <div className="mt-6 grid grid-cols-2 gap-3 sm:max-w-[460px]">
        <Tile label="Views" now={stats.totals.views} before={stats.previous.views} days={stats.days} />
        <Tile label="Visitors" now={stats.totals.visitors} before={stats.previous.visitors} days={stats.days} />
      </div>

      <section className="mt-12">
        <h2 className="text-[15px] font-semibold text-ink">Views by day</h2>
        <p className="mt-0.5 text-[13px] text-ink-2">{span}</p>
        <Daily daily={stats.daily} />
      </section>

      <div className="mt-12 grid gap-12 md:grid-cols-2">
        <Table
          title="Pages"
          rows={stats.pages.map((p) => ({ key: p.path, label: pageLabel(p.path), detail: pageLabel(p.path) === p.path ? undefined : p.path, ...p }))}
          empty="No page has been opened yet."
        />
        <Table
          title="Came from"
          rows={stats.referrers.map((r) => ({ key: r.host ?? "", label: r.host ?? "Direct, or nowhere named", ...r }))}
          empty="Nobody has arrived yet."
        />
      </div>

      <div className="mt-12 grid gap-12 md:grid-cols-2">
        <Table
          title="Visitors by source"
          head="Source"
          rows={stats.sources.map((s) => ({ key: s.source, label: SOURCE_LABEL[s.source], ...s }))}
          empty="Nobody has arrived yet."
        />
        <section>
          <h2 className="text-[15px] font-semibold text-ink">Devices</h2>
          <Devices devices={stats.devices} total={stats.totals.views} />
        </section>
      </div>
    </>
  );
}

/** How the tools are used: which, and for what. Only what a reader did, never who. */
function ToolUsage({ days }: { days: StatsRange }) {
  const { data, isLoading, error } = useToolUsage(days);
  return (
    <section className="mt-16 border-t border-rule pt-10">
      <h2 className="font-serif text-[28px] font-semibold tracking-[-0.01em]">Tool usage</h2>
      <p className="mt-1.5 max-w-[620px] text-[13.5px] leading-relaxed text-ink-2">
        What readers do beyond reading: search, the study panels, audio, their highlights, notes and bookmarks, sign-ins
        and glossary words. The counts by tool are always the last 7 and 30 days; the rest follows the range above.
      </p>
      {isLoading && <p className="mt-6 text-[13px] text-ink-3">Counting…</p>}
      {error && <p className="mt-6 text-[13px] text-ink-2">Couldn’t load tool usage: {error.message}</p>}
      {data && <ToolReport usage={data} />}
    </section>
  );
}

function ToolReport({ usage }: { usage: ToolUsageView }) {
  const span = `${dayLabel(usage.from)} – ${dayLabel(usage.to)}`;
  return (
    <>
      <div className="mt-8">
        <Grid
          title="Uses by tool"
          head={["Tool", "7 days", "Visitors", "30 days", "Visitors"]}
          rows={usage.types.map((t) => ({
            key: t.type,
            cells: [TOOL_LABEL[t.type], format(t.last7.events), format(t.last7.visitors), format(t.last30.events), format(t.last30.visitors)],
          }))}
          empty=""
        />
      </div>

      <section className="mt-12">
        <h3 className="text-[15px] font-semibold text-ink">Uses by day</h3>
        <p className="mt-0.5 text-[13px] text-ink-2">{span}</p>
        <Daily daily={usage.daily.map((d) => ({ day: d.day, views: d.events, visitors: d.visitors }))} noun="use" />
      </section>

      <div className="mt-12 grid gap-12 md:grid-cols-2">
        <Grid
          title="Top searches"
          head={["Search", "Times", "Found nothing"]}
          rows={usage.searches.map((s) => ({ key: s.term, cells: [s.term, format(s.searches), format(s.empty)] }))}
          empty="Nobody has searched yet."
        />
        <Grid
          title="Study panels: verses"
          head={["Verse", "Uses", "Visitors"]}
          rows={usage.verses.map((v) => ({ key: v.ref, cells: [verseLabel(v.ref), format(v.events), format(v.visitors)] }))}
          empty="No study panel has been used yet."
        />
      </div>
      <div className="mt-12 md:max-w-[50%] md:pr-6">
        <Grid
          title="Word study: words"
          head={["Word", "Uses", "Visitors"]}
          rows={usage.words.map((w) => ({ key: w.id, cells: [`${w.word} · ${w.id}`, format(w.events), format(w.visitors)] }))}
          empty="No word has been looked up yet."
        />
      </div>
    </>
  );
}

/** A small table: a label column, then numbers. */
function Grid({ title, head, rows, empty }: { title: string; head: string[]; rows: { key: string; cells: string[] }[]; empty: string }) {
  return (
    <section>
      <h3 className="text-[15px] font-semibold text-ink">{title}</h3>
      {rows.length === 0 ? (
        <p className="mt-2 text-[13px] text-ink-3">{empty}</p>
      ) : (
        <table className="mt-3 w-full border-collapse text-[13px]">
          <thead>
            <tr className="text-[11px] tracking-[0.06em] text-ink-3 uppercase">
              {head.map((h, i) => (
                <th key={h + i} scope="col" className={`pb-1.5 font-normal ${i === 0 ? "text-left" : "w-px pl-4 text-right whitespace-nowrap"}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-t border-rule">
                {row.cells.map((cell, i) => (
                  <td
                    key={i}
                    className={i === 0 ? "max-w-0 py-1.5 pr-3" : `py-1.5 pl-4 text-right whitespace-nowrap tabular-nums ${i === 1 ? "text-ink" : "text-ink-2"}`}
                  >
                    {i === 0 ? <span className="block truncate text-ink">{cell}</span> : cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** A headline number against the same span before it. */
function Tile({ label, now, before, days }: { label: string; now: number; before: number; days: number }) {
  let delta: string;
  if (before === 0) delta = now === 0 ? "none either way" : `none in the ${days} days before`;
  else {
    const pct = Math.round(((now - before) / before) * 100);
    delta = `${pct > 0 ? "+" : pct < 0 ? "−" : "±"}${format(Math.abs(pct))}% on the ${days} days before`;
  }
  return (
    <div className="rounded-lg border border-rule px-4 py-3.5">
      <span className="block text-[12px] tracking-[0.06em] text-ink-3 uppercase">{label}</span>
      <span className="mt-1 block text-[34px] leading-none font-semibold text-ink">{format(now)}</span>
      <span className="mt-2 block text-[12px] text-ink-2">{delta}</span>
    </div>
  );
}

function Daily({ daily, noun = "view" }: { daily: StatsView["daily"]; noun?: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const n = daily.length;
  const max = Math.max(...daily.map((d) => d.views));
  const ticks = niceTicks(max);
  const top = ticks.at(-1)!;
  const grid = { gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, columnGap: `min(2px, ${40 / n}%)` };
  const centre = (i: number) => `${((i + 0.5) / n) * 100}%`;
  const shift = (i: number) => (i / n < 0.15 ? "-10%" : i / n > 0.85 ? "-90%" : "-50%");
  // Date labels far enough apart to read, the last day always among them —
  // and every other one dropped on a phone, where a year's would run together.
  const step = n <= 7 ? 1 : n <= 31 ? 7 : n <= 92 ? 14 : 61;
  const labelled = (i: number) => (n - 1 - i) % step === 0;
  const onPhone = (i: number) => ((n - 1 - i) / step) % 2 === 0;

  return (
    <div>
      <div className="relative mt-6 ml-9 h-44" onPointerLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <div key={t} className={`absolute inset-x-0 border-t ${t === 0 ? "border-ink-3" : "border-rule"}`} style={{ bottom: `${(t / top) * 100}%` }}>
            <span className="absolute top-0 right-full mr-2 -translate-y-1/2 text-[11px] text-ink-3 tabular-nums">{format(t)}</span>
          </div>
        ))}
        <div className="absolute inset-0 grid items-end" style={grid}>
          {daily.map((d, i) => (
            <div key={d.day} className="flex h-full items-end justify-center" onPointerEnter={() => setHover(i)}>
              <div
                className={`w-full max-w-6 rounded-t-[4px] bg-accent transition-opacity ${hover === i ? "opacity-75" : ""}`}
                style={{ height: `${(d.views / top) * 100}%` }}
              />
            </div>
          ))}
        </div>
        {hover !== null && (
          <div
            className="pointer-events-none absolute bottom-full z-10 mb-1 rounded-md border border-rule bg-paper px-2.5 py-1.5 text-[12px] whitespace-nowrap shadow-pop"
            style={{ left: centre(hover), transform: `translateX(${shift(hover)})` }}
          >
            <span className="font-semibold text-ink tabular-nums">{format(daily[hover]!.views)}</span>{" "}
            <span className="text-ink-2">{daily[hover]!.views === 1 ? noun : `${noun}s`} · {format(daily[hover]!.visitors)} {daily[hover]!.visitors === 1 ? "visitor" : "visitors"} · {dayLabel(daily[hover]!.day, true)}</span>
          </div>
        )}
      </div>
      <div className="mt-1.5 ml-9 grid text-[11px] leading-tight text-ink-3" style={grid}>
        {daily.map((d, i) => (
          <span key={d.day} className="flex justify-center">
            {labelled(i) && <span className={`whitespace-nowrap ${onPhone(i) ? "" : "hidden sm:inline"}`}>{dayLabel(d.day)}</span>}
          </span>
        ))}
      </div>
      <details className="mt-4 text-[12.5px]">
        <summary className="cursor-pointer text-ink-3 hover:text-ink">Every day, counted</summary>
        <ol className="mt-2 columns-2 gap-8 sm:columns-3">
          {daily.map((d) => (
            <li key={d.day} className="flex break-inside-avoid justify-between gap-3 border-b border-rule py-0.5">
              <span className="text-ink-2">{dayLabel(d.day)}</span>
              <span className="text-ink tabular-nums">
                {format(d.views)} <span className="text-ink-3">· {format(d.visitors)}</span>
              </span>
            </li>
          ))}
        </ol>
      </details>
    </div>
  );
}

function Table({ title, head, rows, empty }: { title: string; head?: string; rows: ({ key: string; label: string; detail?: string } & StatsTotals)[]; empty: string }) {
  return (
    <section>
      <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-2 text-[13px] text-ink-3">{empty}</p>
      ) : (
        <table className="mt-3 w-full border-collapse text-[13px]">
          <thead>
            <tr className="text-[11px] tracking-[0.06em] text-ink-3 uppercase">
              <th scope="col" className="pb-1.5 text-left font-normal">{head ?? (title === "Pages" ? "Page" : "Site")}</th>
              <th scope="col" className="w-px pb-1.5 pl-4 text-right font-normal">Views</th>
              <th scope="col" className="w-px pb-1.5 pl-4 text-right font-normal">Visitors</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-t border-rule">
                <td className="max-w-0 py-1.5 pr-3">
                  <span className="block truncate text-ink">{row.label}</span>
                  {row.detail && <span className="block truncate text-[11.5px] text-ink-3">{row.detail}</span>}
                </td>
                <td className="py-1.5 pl-4 text-right align-top whitespace-nowrap text-ink tabular-nums">{format(row.views)}</td>
                <td className="py-1.5 pl-4 text-right align-top whitespace-nowrap text-ink-2 tabular-nums">{format(row.visitors)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Devices({ devices, total }: { devices: StatsView["devices"]; total: number }) {
  if (devices.length === 0) return <p className="mt-2 text-[13px] text-ink-3">Nothing yet.</p>;
  const max = Math.max(...devices.map((d) => d.views));
  return (
    <table className="mt-3 w-full max-w-[560px] border-collapse text-[13px]">
      <thead className="sr-only">
        <tr>
          <th scope="col">Device</th>
          <th scope="col">Views</th>
        </tr>
      </thead>
      <tbody>
        {devices.map((d) => (
          <tr key={d.device} className="group">
            <th scope="row" className="w-px py-[3px] pr-3 text-left font-normal whitespace-nowrap text-ink-2 capitalize">{d.device}</th>
            <td className="py-[3px]">
              <div className="flex items-center gap-1.5">
                <div className="h-3.5 shrink-0 rounded-r-[4px] bg-accent transition-opacity group-hover:opacity-75" style={{ width: `max(2px, calc((100% - 14ch) * ${d.views / max}))` }} />
                <span className="whitespace-nowrap text-ink-2 tabular-nums">
                  {format(d.views)} · {Math.round((d.views / total) * 100)}%
                </span>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
